import { Clipboard } from '@angular/cdk/clipboard'
import { Component } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import { environment } from '../../../../../environments/environment'
import { Wish, Wishlist, WishlistGQL, WishlistStatus } from '../../../../../graphql'
import { dialogWidth } from '../../../../constant'
import { Amplitude } from '../../../../injectable'
import { UnsubscribeOnDestroy } from '../../../../utility'
import { storeAsActiveWishlist } from '../serialized-wishlist'
import { UpdateWishlistComponent } from '../update-wishlist/update-wishlist'
import { RemoveWishFromWishlistCommand, RemoveWishFromWishlistGQL } from '../../../../../graphql'
import { CurrencyService } from '../../../../injectable/currency-service'

@Component({
    selector: 'app-wishlist',
    templateUrl: 'wishlist.html'
})
export class WishlistComponent extends UnsubscribeOnDestroy {
    wishlist: Wishlist
    wishlistId: string | null
    dataSource: MatTableDataSource<Wish>
    columnsToDisplay = ['photo', 'name', 'location', 'dates', 'price', 'brand', 'actions'];

    WishlistStatus = WishlistStatus
    currency: string
    companyName: string
    companyLogo: string

    constructor(private _amplitude: Amplitude,
                private _activatedRoute: ActivatedRoute,
                private _dialog: MatDialog,
                private _clipboard: Clipboard,
                private _wishlistGQL: WishlistGQL,
                private _router: Router,
                private _snackBar: MatSnackBar,
                private _removeFromWishlistGQL: RemoveWishFromWishlistGQL,
                private _currencyService: CurrencyService) {
        super()

        // Read wishlistId from arguments and load wishlist using GQL
        this._subscriptions.push(this._activatedRoute.paramMap.subscribe(paramMap => {
            this.wishlistId = paramMap.get('wishlistId')
            if (this.wishlistId) {
                this.refreshWishlist(this.wishlistId)
            }
        }))

        this.currency = this._activatedRoute.snapshot.queryParams['currency']
        this.companyLogo = this._activatedRoute.snapshot.queryParams['company_logo']
        this.companyName = this._activatedRoute.snapshot.queryParams['company_name']

        this._currencyService.presentmentCurrency$.subscribe(currency => {
            this.currency = currency
        })

    }

    get isEmpty(): boolean {
        return this.dataSource?.data.length === 0;
    }

    startWorkOnThisList() {
        storeAsActiveWishlist(this.wishlist, this._amplitude, this._router)
    }

    updateWishlist() {
        let data = {
            data: {wishlist: this.wishlist},
            width: dialogWidth.xs
        };
        this._subscriptions.push(
            this._dialog.open(UpdateWishlistComponent, data)
                .afterClosed()
                .subscribe((wishlistId: string) => {
                    setTimeout(() => this.refreshWishlist(wishlistId), 400)
                }))
    }

    navigateTo(wish: Wish) {
        const listingUrl = wish?.travelDates?.checkin && wish?.travelDates?.checkout ? 
            `/listing/${wish.searchListing.listingId}/${wish.travelDates.checkin}/${wish.travelDates.checkout}` : 
            `/listing/${wish.searchListing.listingId}`

        this._router.navigateByUrl(listingUrl)
    }

    coverImageUrl(wish: Wish): string {
        const coverPhoto = wish.searchListing.summary.listingPhotos.find(photo => photo.useAsCover) || wish.searchListing.summary.listingPhotos[0]
        return coverPhoto?.url
    }

    listingName(wish: Wish) {
        return wish.searchListing?.summary?.title ?? ''
    }

    listingSuite(wish: Wish) {
        return wish.searchListing?.summary?.subtitle ?? ''
    }

    listingCity(wish: Wish) {
        return wish.searchListing?.summary?.city ?? ''
    }

    listingCountry(wish: Wish) {
        return wish.searchListing?.summary?.countryCode ?? ''
    }

    bookingFeeLowest(wish: Wish) {
        return wish.searchListing?.priceRange?.lowest?.bookingFee?.amountMicros ?? 0 / 1000000
    }

    bookingFeeHighest(wish: Wish) {
        return wish.searchListing.priceRange?.highest?.bookingFee?.amountMicros ?? 0 / 1000000
    }

    brand(wish: Wish) {
        return wish.searchListing?.operator?.name ?? ''
    }

    copyLink() {
        this._clipboard.copy(this.shareLink())
        this._snackBar.open('Share link copied to your clipboard')
    }

    removeFromWishlist(wishId: string) {
        const command: RemoveWishFromWishlistCommand = {
            wishlistId: this.wishlistId,
            wishId: wishId,
        }
        this._removeFromWishlistGQL.mutate({input: command}).subscribe(() => {
            this.refreshWishlist(this.wishlistId)
        })
    }

    getQueryParams() {
        return `?currency=${this.currency}&company_name=${encodeURIComponent(this.companyName)}${this.companyLogo ? '&company_logo=' + encodeURIComponent(this.companyLogo) : ''}`
    }

    shareLink(): string {
        return `${environment.memberUrl}public-wishlist/${this.wishlist.wishlistId}${this.getQueryParams()}`
    }

    emailLink() {
        location.href = `mailto:?subject=${this.wishlist.name}&body=${encodeURIComponent(this.shareLink())}`
    }

    previewWishlist() {
        window.open(this.shareLink(), '_blank')
    }   

    private refreshWishlist(wishlistId: string) {
       this._wishlistGQL.fetch({ wishlistId: wishlistId }, { fetchPolicy: 'no-cache' })
            .subscribe(result => {
                if (!result.loading) {
                    if (result.errors?.length) {
                        throw new Error(`Failed to fetch wishlist: ${result.errors[0].message}`)
                    }
                    this.wishlist = result.data.wishlist as Wishlist
                    this.dataSource = new MatTableDataSource(this.wishlist.wishes as Wish[])

                    // this is only to trigger the overflow-container component as the table loads after it inits
                    window.dispatchEvent(new Event('resize'));
                }
            })
    }
}
